/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { Injectable } from '@angular/core';

import { SystemVarsRestService } from '@gh/builder-shared-data';
import { map$, switchMap$ } from '@gh/rx/operators';

import { Observable } from 'rxjs';

import {
  SystemVarsAccountsIntranet, SystemVarsAccountsIntranetTaskProgress,
  SystemVarsAccountsTwilio,
  SystemVarsAccountsXero,
  SystemVarsAccountsXeroSync,
  SystemVarsAccountsXeroSyncProgress,
  SystemVarsAccountsXeroUrl,
  WithIntranetTaskProgress,
  WithXeroSyncProgress,
} from './model/system-vars-accounts.model';

@Injectable()
export class SystemVarsAccountsTabBusinessService {

  constructor(private restService: SystemVarsRestService) {
  }

  initTwilioView(): any {
    return {
      originalTwilio: this.restService.getAccountsTwilioSettings(),
    };
  }

  initXeroView(): any {
    return {
      originalXero: this.getXeroSettings(),
    };
  }

  initIntranetView(): any {
    return this.withIntranetTaskProgress(this.restService.getAccountsIntranetSettings());
  }

  getIntranetTaskProgress(): Observable<SystemVarsAccountsIntranetTaskProgress> {
    return this.restService.getIntranetManagerContractsUpdateBulkJob();
  }

  updateNexmo(settings: SystemVarsAccountsTwilio): Observable<SystemVarsAccountsTwilio> {
    return this.restService.updateAccountsTwilioSettings(settings);
  }

  getXeroSettings(): Observable<any> {
    return this.withXeroSyncProgress(this.restService.getAccountsXeroSettings());
  }

  connectXero(code: string): Observable<SystemVarsAccountsXero> {
    return this.withXeroSyncProgress(this.restService.connectAccountsXero(code));
  }

  modifyXero(settings: any): Observable<SystemVarsAccountsXeroUrl> {
    return this.withXeroSyncProgress(this.restService.modifyAccountsXero(settings));
  }

  getXeroWebhookUrl(): Observable<any> {
    return this.withXeroSyncProgress(this.restService.getAccountsXeroWebhookUrl());
  }

  syncWithXero(syncOptions: SystemVarsAccountsXeroSync): Observable<WithXeroSyncProgress<{}>> {
    return this.withXeroSyncProgress(this.restService.syncAccountsXero(syncOptions));
  }

  getXeroSyncProgress(): Observable<SystemVarsAccountsXeroSyncProgress> {
    return this.restService.checkSyncProgress();
  }

  restartXeroSync(): Observable<WithXeroSyncProgress<{}>> {
    return this.withXeroSyncProgress(this.restService.restartXeroSync());
  }

  saveXeroWebhookKey(webhookKey: any): Observable<any> {
      return this.withXeroSyncProgress(this.restService.saveXeroWebhookKey(webhookKey));
  }

  generateXeroAcceptUrl(settings: SystemVarsAccountsXero): Observable<SystemVarsAccountsXeroUrl> {
    return this.restService.getXeroAcceptUrl(settings);
  }

  updateIntranet(settings: SystemVarsAccountsIntranet): Observable<SystemVarsAccountsIntranet> {
    return this.restService.updateAccountsIntranetSettings(settings);
  }

  updateIntranetManagerContractsUpdateBulkJob(settings: SystemVarsAccountsIntranet): Observable<any> {
    return this.withIntranetTaskProgress(this.restService.updateIntranetManagerContractsUpdateBulkJob(settings));
  }

  private withXeroSyncProgress<T>(response$: Observable<T>): Observable<WithXeroSyncProgress<T>> {
    return response$.pipe(
      switchMap$((response) => this.restService.checkSyncProgress().pipe(map$((progress) => ({
        ...<any>response,
        progress,
      })))));
  }

  private withIntranetTaskProgress<T>(response$: Observable<T>): Observable<WithIntranetTaskProgress> {
    return response$.pipe(
      switchMap$((response) => this.restService.getIntranetManagerContractsUpdateBulkJob().pipe(map$((progress) => ({
        originalIntranet: <any>response,
        progress,
      })))));
  }
}
